import {
  Button,
  DataTable,
  EmptyState,
  Input,
  LoadingSkeleton,
  StatusBadge,
  toast,
  formatDateTime,
} from '@berky/ui'
import { useQuery } from '@tanstack/react-query'
import { ClipboardList, RefreshCw } from 'lucide-react'
import { useMemo, useState } from 'react'

import { findModule } from '../modules'
import { usePlatformApi, type RollenAuditEintrag } from './api'

/** Eine lesbare Historie der Rollenänderungen für Administratoren. */
export function AuditLogTab() {
  const api = usePlatformApi()
  const [suche, setSuche] = useState('')
  const protokoll = useQuery({
    queryKey: ['verwaltung', 'audit-log'],
    queryFn: ({ signal }) =>
      api.get<RollenAuditEintrag[]>('/audit-log', { query: { limit: 50 }, signal }),
  })

  const eintraege = useMemo(() => {
    const begriff = suche.trim().toLocaleLowerCase('de')
    if (!begriff) return protokoll.data ?? []

    return (protokoll.data ?? []).filter((eintrag) =>
      [eintrag.action, eintrag.roleName, eintrag.targetName, eintrag.actorName].some(
        (wert) => wert.toLocaleLowerCase('de').includes(begriff),
      ),
    )
  }, [protokoll.data, suche])

  if (protokoll.isPending) {
    return <LoadingSkeleton variant="table" rows={6} columns={4} />
  }

  return (
    <div className="space-y-4 pt-4">
      <div className="flex flex-wrap items-end justify-between gap-3 rounded-lg border bg-muted/30 p-4">
        <div className="flex items-start gap-3">
          <ClipboardList className="mt-0.5 size-5 text-primary" aria-hidden="true" />
          <div>
            <p className="font-medium">Änderungen nachvollziehen</p>
            <p className="text-sm text-muted-foreground">
              Hier sehen Sie, wer Rollen direkt vergeben, entzogen oder über Gruppen
              verknüpft hat.
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={() => void protokoll.refetch()}
          disabled={protokoll.isFetching}
        >
          <RefreshCw aria-hidden="true" />
          Aktualisieren
        </Button>
      </div>

      <Input
        value={suche}
        onChange={(event) => setSuche(event.target.value)}
        placeholder="Protokoll durchsuchen: Person, Gruppe oder Rolle"
        aria-label="Protokoll durchsuchen"
      />

      <DataTable<RollenAuditEintrag>
        caption="Änderungsprotokoll für Rollen und Gruppenrechte"
        rows={eintraege}
        rowKey={(eintrag) => eintrag.id}
        isLoading={protokoll.isFetching}
        error={
          protokoll.isError
            ? {
                onRetry: () => {
                  toast.error('Das Änderungsprotokoll konnte nicht geladen werden')
                  void protokoll.refetch()
                },
              }
            : null
        }
        emptyState={
          <EmptyState
            title={
              suche ? 'Keine Änderung gefunden' : 'Noch keine Änderungen protokolliert'
            }
            description={
              suche
                ? 'Verwenden Sie einen anderen Suchbegriff.'
                : 'Neue Rollenänderungen erscheinen hier automatisch.'
            }
          />
        }
        columns={[
          {
            key: 'zeitpunkt',
            header: 'Zeitpunkt',
            cell: (eintrag) => (
              <span className="whitespace-nowrap text-sm">
                {formatDateTime(eintrag.occurredAt)}
              </span>
            ),
          },
          {
            key: 'aktion',
            header: 'Änderung',
            cell: (eintrag) => (
              <StatusBadge
                tone={
                  eintrag.action.includes('entzogen') || eintrag.action.includes('gelöst')
                    ? 'warning'
                    : 'info'
                }
              >
                {eintrag.action}
              </StatusBadge>
            ),
          },
          {
            key: 'ziel',
            header: 'Ziel',
            cell: (eintrag) => (
              <div>
                <p className="font-medium">{eintrag.targetName}</p>
                <p className="text-xs text-muted-foreground">
                  {eintrag.targetType === 'gruppe' ? 'Gruppe' : 'Person'}
                </p>
              </div>
            ),
          },
          {
            key: 'rolle',
            header: 'Rolle',
            cell: (eintrag) => (
              <div>
                <p className="font-medium">{eintrag.roleName}</p>
                <p className="text-xs text-muted-foreground">
                  {findModule(eintrag.roleName.split('.')[0] ?? '')?.title ?? 'Plattform'}
                </p>
              </div>
            ),
          },
          {
            key: 'ausloeser',
            header: 'Durch',
            cell: (eintrag) => (
              <div>
                <p>{eintrag.actorName}</p>
                {eintrag.actorName !== eintrag.actorId && (
                  <p className="text-xs text-muted-foreground">{eintrag.actorId}</p>
                )}
              </div>
            ),
          },
        ]}
      />
    </div>
  )
}
